import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  Card,
  CardHeader,
  CardBody,
  Flex,
  Heading,
  Text,
  Skeleton,
  Button,
  useModal,
  Box,
  CardFooter,
  ExpandableLabel,
} from '@pancakeswap/uikit'
import { useWeb3React } from '@pancakeswap/wagmi'
import { LotteryStatus } from 'config/constants/types'
import { useTranslation } from '@pancakeswap/localization'
import { useEthPriceBusd, usePriceCakeBusd } from 'state/farms/hooks'
import { useLottery } from 'state/lottery/hooks'
import { getBalanceNumber } from 'utils/formatBalance'
import Balance from 'components/Balance'
import ViewTicketsModal from './ViewTicketsModal'
import BuyTicketsButton from './BuyTicketsButton'
import { dateTimeOptions } from '../helpers'
import RewardBrackets from './RewardBrackets'
import BogoIcon from '../svgs/Bogo'
import useNextEventCountdown from '../hooks/useNextEventCountdown'
import getTimePeriods from 'utils/getTimePeriods'
import BigNumber from 'bignumber.js'
import { useCakeBusdPrice } from 'hooks/useBUSDPrice'
import { useWindowDimensions } from 'state/wallet/hooks'

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-column-gap: 32px;
    grid-template-columns: auto 1fr;
  }
`

const StyledCard = styled(Card)`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.10); background: rgba(255, 255, 255, 0.01);
  box-shadow: 0px 0px 0px 10px rgba(255, 255, 255, 0.03) inset;
  border-radius: 38px;
  padding: 8px;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 520px;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    width: 680px;
  }
`

const StyledCardInner = styled.div`
  width: 100%;
  border-radius: 30px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: radial-gradient(70.71% 70.71% at 50% 50%, rgba(148, 101, 255, 0.04) 0%, rgba(148, 101, 255, 0.00) 100%), rgba(4, 1, 21, 0.10); 
  display: flex;
  flex-direction: column;
`

const NextDrawWrapper = styled.div`
  padding: 20px;
`

const StyledBogoContainer = styled.div`
  display: flex;
  padding: 0px 20px 20px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px; 
  border-radius: 30px;
  border: 1px solid rgba(243, 238, 255, 0.08);
  background: linear-gradient(180deg, rgba(243, 238, 255, 0.00) 0%, rgba(243, 238, 255, 0.04) 100%), rgba(147, 130, 255, 0.01);
  box-shadow: 0px 0px 12px 0px rgba(255, 255, 255, 0.08) inset; 
  position: relative;
`

const BogoHeader = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  left: 50%;
  font-size: 14px;
  transform: translateX(-50%);
  padding: 7px 8px;
  border-radius: 0px 0px 12px 12px;
  border: 1px solid rgba(255, 102, 102, 0.15);
  background: linear-gradient(180deg, rgba(255, 102, 102, 0.65) 0%, rgba(255, 102, 102, 0.36) 100%), rgba(147, 130, 255, 0.01);
  box-shadow: 0px 0px 7px 0px ${({ theme }) => theme.colors.red} inset;
  width: max-content;
`

const NextDrawCard = () => {
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const { account } = useWeb3React()
  const { currentLotteryId, isTransitioning, currentRound } = useLottery()
  const { endTime, amountCollectedInEth, userTickets, status, albAmountPreAllocated } = currentRound

  const [onPresentViewTicketsModal] = useModal(<ViewTicketsModal roundId={currentLotteryId} roundStatus={status} />)
  const [isExpanded, setIsExpanded] = useState(false)
  const ticketBuyIsDisabled = status !== LotteryStatus.OPEN || isTransitioning
  const secondsRemaining = useNextEventCountdown(parseInt(currentRound?.endTime))
  const { days, hours, minutes } = getTimePeriods(secondsRemaining)

  const ethPrice = useEthPriceBusd({forceMainnet: true})
  const cakePrice = usePriceCakeBusd()
  const prizeInBusd = amountCollectedInEth.times(ethPrice).plus(new BigNumber(albAmountPreAllocated).times(new BigNumber(cakePrice?.toString())))
  const endTimeMs = parseInt(endTime, 10) * 1000
  const endDate = new Date(endTimeMs)
  const isLotteryOpen = status === LotteryStatus.OPEN
  const userTicketCount = userTickets?.tickets?.length || 0
  const getPrizeBalances = () => {
    if (status === LotteryStatus.CLOSE || status === LotteryStatus.CLAIMABLE) {
      return (
        <Heading scale="xl" color="text" textAlign={['center', null, null, 'left']}>
          {t('Calculating')}...
        </Heading>
      )
    }
    return (
      <>
        {prizeInBusd.isNaN() ? (
          <Skeleton my="7px" height={40} width={160} />
        ) : (
          <Balance
            fontSize="32px"
            color="success"
            textAlign='center'
            lineHeight="1"
            bold
            fontFamily="Syncopate, sans-serif"
            prefix="~$"
            value={getBalanceNumber(prizeInBusd)}
            decimals={0}
            mb='20px'
          />
        )}
        <Flex style={{gap: '5px'}}>
        {prizeInBusd.isNaN() ? (
          <Skeleton my="2px" height={14} width={90} />
        ) : (
          <Flex justifyContent='center'>
            <Balance
              fontSize="18px"
              color="text"
              textAlign='center'
              value={getBalanceNumber(amountCollectedInEth)}
              decimals={4}
            />
            <Text ml='5px'>{'ETH'}</Text>
          </Flex>
        )}
        <Text textAlign='center'>+</Text>
        {prizeInBusd.isNaN() ? (
          <Skeleton my="2px" height={14} width={90} />
        ) : (
          <Flex justifyContent='center'>
          <Balance
            fontSize="19px"
            color="text"
            textAlign='center'
            value={getBalanceNumber(new BigNumber(albAmountPreAllocated))}
            decimals={0}
          />
          <Text ml='5px'>{'ALB'}</Text>
          </Flex>
        )}
        </Flex>
      </>
    )
  }

  const getNextDrawId = () => {
    if (status === LotteryStatus.OPEN) {
      return `${currentLotteryId} |`
    }
    if (status === LotteryStatus.PENDING) {
      return ''
    }
    return parseInt(currentLotteryId, 10) + 1
  }

  const getNextDrawDateTime = () => {
    if (status === LotteryStatus.OPEN) {
      return `${t('Extraction in')} ${days}d ${hours}h ${minutes}m`
    }
    return ''
  }

  const theme = useTheme()
  const {width} = useWindowDimensions()

  const ticketRoundText =
    userTicketCount > 1
      ? t('You have %amount% tickets this round', { amount: userTicketCount })
      : t('You have %amount% ticket this round', { amount: userTicketCount })
  const [youHaveText, ticketsThisRoundText] = ticketRoundText.split(userTicketCount.toString())

  return (
    <StyledCard>
      <StyledCardInner>
        <CardHeader p="20px" style={{borderBottom: `1px solid ${theme.colors.cardBorder}`}} mb='60px'>
          <Flex justifyContent="space-between">
            <Heading mr="12px">{t('Next Draw')}</Heading>
            <Text>
              {currentLotteryId && `#${getNextDrawId()}`} {Boolean(endTime) && getNextDrawDateTime()}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody p='0'>
          <Flex flexDirection='column' justifyContent='center' alignItems='center'>
            <Flex justifyContent={['center', null, null, 'flex-start']}>
              <Text mb='20px' color='textSubtle'>{t('Prize pot')}</Text>
            </Flex>
            <Flex flexDirection="column" mb="20px" justifyContent='center'>
              {getPrizeBalances()}
            </Flex>
            <StyledBogoContainer>
              <BogoHeader>{'THIS WEEK ONLY'}</BogoHeader>
              <BogoIcon mt='60px' />
              <Text mb='-30px'>{'Buy one ticket'}</Text>
              <Text>{'get one free'}</Text>
              <BuyTicketsButton disabled={ticketBuyIsDisabled} maxWidth="280px" />
            </StyledBogoContainer>
            
            <Flex width='100%' alignItems='center' mb={!isLotteryOpen && '60px'} >
              {isLotteryOpen && (
                <Flex
                  alignItems='center'
                  width='100%'
                  justifyContent={!userTickets.isLoading && userTicketCount > 0 ? 'space-between' : 'center'}
                  borderTop={`1px solid ${theme.colors.cardBorder}`}
                  borderBottom={`1px solid ${theme.colors.cardBorder}`}
                  p='20px'
                  mt='60px'
                >
                  {account && (
                    <Flex justifyContent='center'>
                      <Text fontSize={width >= 500? '18px' : '14px'} color='textSubtle' display="inline">{youHaveText} </Text>
                      {!userTickets.isLoading ? (
                        <Balance fontSize={width >= 500? '18px' : '14px'} value={userTicketCount} decimals={0} display="inline" mx="4px" />
                      ) : (
                        <Skeleton mx="4px" height={20} width={40} />
                      )}
                      <Text fontSize={width >= 500? '18px' : '14px'} color='textSubtle' display="inline"> {ticketsThisRoundText}</Text>
                    </Flex>
                  )}
                  {!userTickets.isLoading && userTicketCount > 0 && (
                    <Button
                      onClick={onPresentViewTicketsModal}
                      height="44px"
                      width="fit-content"
                      p="0"
                      variant="text"
                      scale="sm"
                      style={{
                        border: `1px solid ${theme.colors.cardBorder}`, 
                        padding: '0px 20px', fontSize: `${width >= 500 ? '18px' : '14px'}`,
                         color: `${theme.colors.text}`
                      }}
                    >
                      {t('View your tickets')}
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        </CardBody>
        <CardFooter p="0">
          {isExpanded && (
            <NextDrawWrapper>
              <RewardBrackets lotteryNodeData={currentRound} />
            </NextDrawWrapper>
          )}
          {(status === LotteryStatus.OPEN || status === LotteryStatus.CLOSE) && (
            <Flex alignItems="center" justifyContent="center">
              <ExpandableLabel expanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? t('Hide') : t('Details')}
              </ExpandableLabel>
            </Flex>
          )}
        </CardFooter>
      </StyledCardInner>
    </StyledCard>
  )
}

export default NextDrawCard
